import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { getFormatObject, getViewer } from 'libs/rendering/utils';

import { useRouterStore } from 'apps/hooks';
import { useContextManager } from 'apps/context/hooks';
import { useSharedFileManager } from 'apps/share/hooks';
import { useViewerWidth } from 'libs/rendering/hooks';
import { ViewerControlProvider } from 'libs/rendering/gltf/hooks';
import { makeLoadingMessage } from 'libs/rendering/gltf/viewer';
import { frameStore } from 'apps/components/Frame/appManager';

import { ViewerControl } from 'libs/rendering/gltf/viewerControl';

import { VisibilityWrapper } from 'components';

import ChatPanel from './gltf/components/chat/chatPanel';
import DfmPanel from './gltf/components/dfmAnalysis/dfmPanel';
import DiffPanel from './gltf/components/diff/diffPanel';

// import { ViewerTab } from './gltf/components/tab';

export const PreviewFileViewer = observer(props => {
  const contextManager = useContextManager();
  const [viewerControl] = useState(
    () => new ViewerControl(contextManager, true),
  );

  return (
    <ViewerControlProvider value={viewerControl}>
      {props.children}
    </ViewerControlProvider>
  );
});

export const FileViewer = observer(props => {
  const routerStore = useRouterStore();
  const contextManager = useContextManager();
  const sharedFileManager = useSharedFileManager();

  const { file, partId, isPreview } = props;
  const cadIdFromParams = routerStore.params.get('cadId');
  const partIdFromPropsOrParams = partId || routerStore.params.get('partId');
  const prodIdFromParams = routerStore.params.get('prodId');
  const { profile, initiative } = routerStore.route.params;

  const { formatObject } = getFormatObject(file);
  const { progress } = contextManager?.viewerConversionProgress || {};
  const { status, reason, errorCode } = contextManager?.viewerConversion || {};

  const Viewer = getViewer(formatObject);

  const loadingProps =
    formatObject?.viewer === 'Loading'
      ? {
          text: makeLoadingMessage(progress, status, reason, errorCode),
          noText: false,
        }
      : {};

  const shouldRenderRightPanel = frameStore.canShowRightPanel;

  const isDfmPanelVisible = routerStore.params.get('panel') === 'dfm';
  const isDiffPanelVisible = routerStore.params.get('panel') === 'diff';
  const { userSettingsOpen } = sharedFileManager || {};

  useEffect(() => {
    frameStore.SetFile(file);
    frameStore.SetContextManager(contextManager);
    frameStore.SetSharedFileManager(sharedFileManager);
    return () => {
      frameStore.SetFile(null);
      frameStore.SetContextManager(null);
      frameStore.SetSharedFileManager(null);
    };
  }, [file, contextManager, sharedFileManager]);

  useEffect(() => {
    if (!sharedFileManager || !userSettingsOpen) return;
    if (profile) {
      sharedFileManager.ProfileQuery(profile);
    }

    if (initiative) {
      sharedFileManager.InitiativeQuery(initiative);
    }
  }, [profile, initiative, userSettingsOpen, sharedFileManager]);

  const [viewerWidth] = useViewerWidth();

  if (isPreview) {
    return (
      <PreviewFileViewer>
        {/* {formatObject.viewer !== 'GltfViewer' && <ViewerTab />} */}
        <Viewer
          isPreview={isPreview}
          file={file}
          cadId={cadIdFromParams}
          partId={partIdFromPropsOrParams}
          prodId={prodIdFromParams}
          {...loadingProps}
        />
        {shouldRenderRightPanel && props.children}
      </PreviewFileViewer>
    );
  }

  return (
    <>
      {/* {formatObject.viewer !== 'GltfViewer' && <ViewerTab />} */}
      <Viewer
        isPreview={isPreview}
        file={file}
        cadId={cadIdFromParams}
        partId={partIdFromPropsOrParams}
        prodId={prodIdFromParams}
        width={viewerWidth}
        {...loadingProps}
      />

      {/* Chat should be mounted based on whether a Context is available.
          If Chat is visible, "wins" over any other right tool */}

      <ChatPanel />
      {isDfmPanelVisible && <DfmPanel />}
      {isDiffPanelVisible && <DiffPanel />}
      <VisibilityWrapper visible={shouldRenderRightPanel}>
        {props.children &&
          React.cloneElement(props.children, {
            fileFormatObject: formatObject,
          })}
      </VisibilityWrapper>
    </>
  );
});

FileViewer.propTypes = {
  file: PropTypes.object,
  partId: PropTypes.string,
  children: PropTypes.object,
};
